import { useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { DGetCustomerConversations } from '../data/dtos/conversation';
import { MChatConversation } from '../models/chat-conversation';
import { marketplaceChatService } from '../services/marketplace';

const PAGE_SIZE = 20;

export function useMarketplaceCustomerConversations({
  userId,
  branchId,
}: Nullable<Pick<DGetCustomerConversations, 'userId' | 'branchId'>>) {
  const execute = async ([_, __, ___, page]: [
    string,
    number,
    number,
    number,
  ]) => {
    return await marketplaceChatService.getCustomerConversations({
      userId: userId!,
      branchId,
      page,
    });
  };

  const fetchKeyGenerator = (index: number) => {
    if (!userId) {
      return null;
    }
    const page = index + 1;
    return ['get-marketplace-customer-conversations', userId, branchId, page];
  };
  const { data, error, isLoading, isValidating, mutate, size, setSize } =
    useSWRInfinite(fetchKeyGenerator, execute);

  // Reverse the order of messages to display the latest message at the bottom
  const conversations = useMemo(() => {
    if (!data) {
      return [];
    }
    if (data.length > 0) {
      data[data.length - 1].reduce<MChatConversation[]>(
        (activeConversations, conversation) => {
          const existingIndex = activeConversations.findIndex(
            (c) => c.props.id === conversation.props.id
          );
          if (existingIndex === -1) {
            activeConversations.push(conversation);
          }
          return activeConversations;
        },
        []
      );
    }
    return data.toReversed().flat();
  }, [data]);

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isRefreshing = (isValidating && data && data.length === size) ?? true;
  const isEmpty = data?.[0]?.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.length < PAGE_SIZE);

  const refresh = async () => await mutate();
  const loadMore = async () => await setSize((size) => size + 1);

  return {
    conversations,
    loadMore,
    refresh,
    error,
    isLoading,
    isLoadingMore,
    isRefreshing,
    isReachingEnd,
  };
}
